/**
 * Date orders, as the formatting locales that produce them.
 *
 * `Intl` will not take DD/MM/YYYY as an instruction — it takes a locale and
 * gives you that locale's field order. So each choice names the locale that
 * produces it, which is the standard way to do this and is honest about what is
 * happening underneath.
 *
 * Here rather than in the fixture because the app reads it — `useApplyOrgFormatting`
 * turns the stored key into the locale `format.js` uses — and a page reaching
 * into `mocks/` would bundle the demo layer into the production build. The
 * fixture imports it from here, which is the same seam `FUSION_STATUSES` uses.
 *
 * The formatting locale is deliberately NOT the interface language: a German
 * plant reading an English interface still wants its own date order.
 */
export const DATE_FORMATS = {
  dmy: 'en-GB',
  mdy: 'en-US',
  ymd: 'sv-SE',
}

export const DATE_FORMAT_KEYS = Object.keys(DATE_FORMATS)

export const TIME_FORMATS = ['h24', 'h12']

/**
 * The API's date-order vocabulary, and this product's.
 *
 * The wire says `dd_mm_yyyy`; the keys above say `dmy`, and that key is what
 * maps to an `Intl` locale. Both directions live here, next to the vocabulary
 * they translate, because more than one resource carries these fields — a USER
 * has them and so does a PLANT, and two copies of this table would be two places
 * for it to drift.
 *
 * An unrecognised value maps to null rather than to a default. A wrong date
 * order is read as a real date — 03/04 is a different day in two of these — so
 * declining beats guessing.
 */
const DATE_ORDER = {
  dd_mm_yyyy: 'dmy',
  mm_dd_yyyy: 'mdy',
  yyyy_mm_dd: 'ymd',
}

const DATE_ORDER_WIRE = Object.fromEntries(
  Object.entries(DATE_ORDER).map(([wire, key]) => [key, wire]),
)

/** Wire → app. `dd_mm_yyyy` → `dmy`. */
export function toDateFormatKey(wire) {
  const key = DATE_ORDER[wire] ?? null
  return key && DATE_FORMAT_KEYS.includes(key) ? key : null
}

/** App → wire. `dmy` → `dd_mm_yyyy`. */
export const toDateFormatWire = (key) => DATE_ORDER_WIRE[key] ?? null

/** `h12` / `h24` already match on both sides. Validated, not translated. */
export const toTimeFormatKey = (wire) => (TIME_FORMATS.includes(wire) ? wire : null)
